import { useState, useRef, useEffect, useCallback } from "react";
import { useAuth } from "@hole/shared/auth";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  toolsCalled?: string[];
}

interface FOIAProfile {
  jurisdiction?: string;
  phase: string;
  completedSteps: string[];
  agencyName?: string;
  hasRequesterInfo?: boolean;
}

interface ChatResponse {
  response: string;
  sessionId: string;
  toolsCalled: string[];
  foiaProfile: FOIAProfile;
  documentId?: string;
}

const INITIAL_MESSAGE: Message = {
  id: "initial",
  role: "assistant",
  content: `Hi! I'm the **HOLE Truth FOIA Generator**. I'll help you create a legally sound public records request.

I can help you:
- Identify the right statute for your state or federal request
- Craft a precise description of the records you need
- Assert your strongest rights of access
- Anticipate and address potential exemptions
- Generate a downloadable Word document ready to send

**Let's get started!** Which state or federal agency do you want to request records from?`,
  timestamp: new Date(),
};

export default function FOIAGenerator() {
  const { user, isAuthenticated } = useAuth();
  const [messages, setMessages] = useState<Message[]>([INITIAL_MESSAGE]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [foiaProfile, setFoiaProfile] = useState<FOIAProfile | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: inputValue.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/foia-generator/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: userMessage.content,
          sessionId,
          userId: user?.sub,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const data: ChatResponse = await response.json();

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: data.response,
        timestamp: new Date(),
        toolsCalled: data.toolsCalled,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setSessionId(data.sessionId);
      setFoiaProfile(data.foiaProfile);

      if (data.documentId) {
        setDocumentId(data.documentId);
      }
    } catch (err) {
      console.error("Chat error:", err);
      setError(
        "Failed to get response. Please check your connection and try again."
      );

      // Add error message to chat
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content:
          "I'm sorry, I encountered an error processing your request. Please try again in a moment.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDownload = () => {
    if (documentId) {
      window.open(`/api/foia-generator/download/${documentId}`, "_blank");
    }
  };

  const handleNewRequest = () => {
    setMessages([INITIAL_MESSAGE]);
    setSessionId(null);
    setFoiaProfile(null);
    setDocumentId(null);
    setError(null);
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Render markdown-like formatting in messages
  const renderMessageContent = (content: string) => {
    // Simple markdown rendering for bold and bullet points
    return content.split("\n").map((line, i) => {
      // Handle bold text
      const formattedLine = line.replace(
        /\*\*(.*?)\*\*/g,
        '<strong class="font-semibold">$1</strong>'
      );

      // Handle bullet points
      if (line.startsWith("- ")) {
        return (
          <li
            key={i}
            className="ml-4 list-disc"
            dangerouslySetInnerHTML={{ __html: formattedLine.slice(2) }}
          />
        );
      }

      return (
        <p
          key={i}
          className={line === "" ? "h-4" : ""}
          dangerouslySetInnerHTML={{ __html: formattedLine }}
        />
      );
    });
  };

  return (
    <div className="min-h-screen bg-brand-martinique">
      <Navigation />

      <div className="w-full bg-brand-martinique py-8 lg:py-12">
        <div className="max-w-[900px] mx-auto px-4 lg:px-8">
          {/* Header */}
          <div className="mb-6 text-center">
            <h1 className="font-agency text-3xl lg:text-4xl text-white mb-2">
              FOIA Request Generator
            </h1>
            <p className="font-muller text-brand-gray-400">
              AI-powered public records request assistant
            </p>
          </div>

          {/* Progress Indicator */}
          {foiaProfile && (
            <div className="mb-6 bg-brand-dark/50 rounded-lg p-4 border border-brand-stroke">
              <div className="flex items-center justify-between mb-2">
                <span className="font-muller text-sm text-brand-gray-400">
                  Request Progress
                </span>
                {foiaProfile.jurisdiction && (
                  <span className="font-jetbrains text-xs text-brand-azure">
                    {foiaProfile.jurisdiction}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                {["jurisdiction", "records", "requester", "exemptions", "complete"].map(
                  (step) => (
                    <div
                      key={step}
                      className={`flex-1 h-2 rounded-full ${
                        foiaProfile.completedSteps?.includes(step)
                          ? "bg-brand-azure"
                          : "bg-brand-dark-3"
                      }`}
                    />
                  )
                )}
              </div>
              <div className="flex justify-between mt-1">
                <span className="font-muller text-xs text-brand-gray-500">
                  Location
                </span>
                <span className="font-muller text-xs text-brand-gray-500">
                  Records
                </span>
                <span className="font-muller text-xs text-brand-gray-500">
                  Profile
                </span>
                <span className="font-muller text-xs text-brand-gray-500">
                  Review
                </span>
                <span className="font-muller text-xs text-brand-gray-500">
                  Done
                </span>
              </div>
            </div>
          )}

          {/* Chat Container */}
          <div className="bg-brand-dark-2 rounded-lg border border-brand-stroke shadow-lg overflow-hidden">
            {/* Chat Header */}
            <div className="bg-brand-dark rounded-t-lg p-4 lg:p-5 border-b border-brand-stroke">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full bg-brand-orange flex items-center justify-center overflow-hidden">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M9.99932 4.32261L8.86476 7.3887C8.61155 8.073 8.07202 8.61252 7.38772 8.86574L4.32163 10.0003L7.38772 11.1348C8.07202 11.3881 8.61155 11.9276 8.86476 12.6119L9.99932 15.678L11.1339 12.6119C11.3871 11.9276 11.9266 11.3881 12.6109 11.1348L15.677 10.0003L12.6109 8.86574C11.9266 8.61252 11.3871 8.073 11.1339 7.3887L9.99932 4.32261ZM10.9372 2.5348C10.615 1.66418 9.38362 1.66418 9.06146 2.5348L7.45798 6.86814C7.3567 7.14186 7.14089 7.35767 6.86717 7.45896L2.53382 9.06244C1.6632 9.3846 1.66321 10.616 2.53382 10.9381L6.86717 12.5416C7.14089 12.6429 7.3567 12.8587 7.45798 13.1324L9.06146 17.4658C9.38362 18.3364 10.615 18.3364 10.9372 17.4658L12.5407 13.1324C12.6419 12.8587 12.8577 12.6429 13.1315 12.5416L17.4648 10.9381C18.3354 10.616 18.3354 9.3846 17.4648 9.06244L13.1315 7.45896C12.8577 7.35767 12.6419 7.14186 12.5407 6.86814L10.9372 2.5348Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-[#22AD5C] rounded-full border-2 border-brand-dark"></div>
                  </div>
                  <div>
                    <h3 className="font-agency text-lg text-white">
                      FOIA Assistant
                    </h3>
                    <p className="font-muller text-sm text-brand-gray-400">
                      {isLoading ? "Thinking..." : "Ready to help"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {documentId && (
                    <button
                      onClick={handleDownload}
                      className="flex items-center gap-2 px-4 py-2 bg-brand-azure rounded-lg hover:bg-brand-azure/90 transition-colors"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 12L3 7L4.4 5.55L7 8.15V0H9V8.15L11.6 5.55L13 7L8 12ZM2 16C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V11H2V14H14V11H16V14C16 14.55 15.8042 15.0208 15.4125 15.4125C15.0208 15.8042 14.55 16 14 16H2Z"
                          fill="white"
                        />
                      </svg>
                      <span className="font-muller text-sm text-white hidden sm:inline">
                        Download Request
                      </span>
                    </button>
                  )}
                  <button
                    onClick={handleNewRequest}
                    className="px-4 py-2 border border-brand-stroke rounded-lg text-brand-gray-400 hover:text-white hover:border-brand-gray-400 transition-colors"
                  >
                    <span className="font-muller text-sm">New Request</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Messages Area */}
            <div className="h-[500px] lg:h-[600px] overflow-y-auto p-4 lg:p-6 space-y-6 bg-brand-martinique/50">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-3 ${
                    message.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  {message.role === "assistant" && (
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-brand-orange flex items-center justify-center">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M9.99932 4.32261L8.86476 7.3887C8.61155 8.073 8.07202 8.61252 7.38772 8.86574L4.32163 10.0003L7.38772 11.1348C8.07202 11.3881 8.61155 11.9276 8.86476 12.6119L9.99932 15.678L11.1339 12.6119C11.3871 11.9276 11.9266 11.3881 12.6109 11.1348L15.677 10.0003L12.6109 8.86574C11.9266 8.61252 11.3871 8.073 11.1339 7.3887L9.99932 4.32261Z"
                            fill="white"
                          />
                        </svg>
                      </div>
                    </div>
                  )}

                  <div
                    className={`flex flex-col max-w-[85%] lg:max-w-[70%] ${
                      message.role === "user" ? "items-end" : "items-start"
                    }`}
                  >
                    <div
                      className={`px-5 py-4 rounded-2xl shadow-sm ${
                        message.role === "user"
                          ? "bg-brand-azure rounded-br-none"
                          : "bg-brand-dark-2 rounded-bl-none border border-brand-dark-3"
                      }`}
                    >
                      <div
                        className={`font-muller text-base leading-relaxed space-y-2 ${
                          message.role === "user"
                            ? "text-white"
                            : "text-brand-gray-100"
                        }`}
                      >
                        {renderMessageContent(message.content)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="font-muller text-xs text-brand-gray-500">
                        {formatTimestamp(message.timestamp)}
                      </span>
                      {message.toolsCalled && message.toolsCalled.length > 0 && (
                        <span className="font-jetbrains text-xs text-brand-azure/70">
                          [{message.toolsCalled.join(", ")}]
                        </span>
                      )}
                    </div>
                  </div>

                  {message.role === "user" && isAuthenticated && (
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-brand-azure flex items-center justify-center overflow-hidden">
                        {user?.picture ? (
                          <img
                            src={user.picture}
                            alt={user.name || "User"}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="font-agency text-sm text-white font-bold">
                            {user?.name?.charAt(0).toUpperCase() || "U"}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Loading indicator */}
              {isLoading && (
                <div className="flex gap-3 justify-start">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-brand-orange flex items-center justify-center">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="animate-pulse"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M9.99932 4.32261L8.86476 7.3887C8.61155 8.073 8.07202 8.61252 7.38772 8.86574L4.32163 10.0003L7.38772 11.1348C8.07202 11.3881 8.61155 11.9276 8.86476 12.6119L9.99932 15.678L11.1339 12.6119C11.3871 11.9276 11.9266 11.3881 12.6109 11.1348L15.677 10.0003L12.6109 8.86574C11.9266 8.61252 11.3871 8.073 11.1339 7.3887L9.99932 4.32261Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="bg-brand-dark-2 rounded-2xl rounded-bl-none border border-brand-dark-3 px-5 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-brand-azure rounded-full animate-bounce"></div>
                      <div
                        className="w-2 h-2 bg-brand-azure rounded-full animate-bounce"
                        style={{ animationDelay: "0.1s" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-brand-azure rounded-full animate-bounce"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Error Banner */}
            {error && (
              <div className="bg-red-900/50 border-t border-red-700 px-4 py-3">
                <p className="font-muller text-sm text-red-200">{error}</p>
              </div>
            )}

            {/* Input Area */}
            <div className="bg-brand-dark p-4 lg:p-5 rounded-b-lg border-t border-brand-stroke">
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={
                      isLoading
                        ? "Waiting for response..."
                        : "Type your message..."
                    }
                    disabled={isLoading}
                    className="w-full px-5 py-4 bg-brand-dark-2 border border-brand-stroke rounded-lg font-muller text-base text-white placeholder:text-brand-gray-500 focus:outline-none focus:border-brand-azure transition-colors disabled:opacity-50"
                  />
                </div>

                <button
                  onClick={handleSend}
                  disabled={isLoading || !inputValue.trim()}
                  className="flex items-center justify-center w-14 h-14 bg-brand-azure rounded-lg hover:bg-brand-azure/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20.4875 10.2094C20.35 9.96875 20.1438 9.79687 19.9031 9.65937L4.05626 0.79062C3.78126 0.65312 3.47188 0.58437 3.16251 0.618745C2.85313 0.65312 2.57813 0.756245 2.33751 0.962495C2.09688 1.16875 1.92501 1.44375 1.85626 1.71875C1.75313 2.02812 1.78751 2.3375 1.89063 2.64687L4.84688 11L1.89063 19.3531C1.78751 19.6625 1.78751 19.9719 1.85626 20.2469C1.92501 20.5562 2.09688 20.7969 2.33751 21.0031C2.57813 21.2094 2.85313 21.3125 3.16251 21.3469C3.19688 21.3469 3.26563 21.3469 3.30001 21.3469C3.54063 21.3469 3.81563 21.2781 4.05626 21.1406L19.9031 12.2719C20.1438 12.1344 20.35 11.9625 20.4875 11.7219C20.625 11.4812 20.6938 11.2062 20.6938 10.9656C20.6938 10.725 20.625 10.45 20.4875 10.2094ZM3.36876 2.16562L17.7375 10.2094H6.22188L3.36876 2.16562ZM3.36876 19.8344L6.25626 11.7906H17.7719L3.36876 19.8344Z"
                      fill="white"
                    />
                  </svg>
                </button>
              </div>

              {/* Quick hints */}
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="font-muller text-xs text-brand-gray-500">
                  Try:
                </span>
                {[
                  "Texas state records",
                  "Federal FOIA request",
                  "Police department emails",
                ].map((hint) => (
                  <button
                    key={hint}
                    onClick={() => setInputValue(hint)}
                    disabled={isLoading}
                    className="px-3 py-1 bg-brand-dark-3 rounded-full font-muller text-xs text-brand-gray-400 hover:text-white hover:bg-brand-dark-2 transition-colors disabled:opacity-50"
                  >
                    {hint}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Footer info */}
          <div className="mt-6 text-center">
            <p className="font-muller text-sm text-brand-gray-500">
              This tool generates public records requests based on publicly
              available statutes. It does not provide legal advice.
            </p>
            <p className="font-muller text-xs text-brand-gray-600 mt-2">
              Powered by Claude AI • Data from The HOLE Foundation transparency
              database
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
